// DESCRIPTION: Step 4 — notification toggles. Three email switches plus an
// opt-in switch for anonymous usage data. Templates configured later.

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import WizardShell from '../components/WizardShell';

/**
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.value       { admin_notify, customer_confirm, reminder, telemetry }
 * @param {Function} props.onChange    (next) => void
 * @param {Function} props.onBack
 * @param {Function} props.onSkip      Skip this step.
 * @param {Function} props.onSkipSetup Skip the entire setup.
 * @param {Function} props.onContinue
 * @param {number}   props.progress
 * @param {boolean}  props.isLoading
 *
 * @return {JSX.Element}
 */
export default function NotificationsStep( {
	value,
	onChange,
	onBack,
	onSkip,
	onSkipSetup,
	onContinue,
	progress,
	isLoading,
} ) {
	const [ showCollected, setShowCollected ] = useState( false );

	const toggle = ( key ) => onChange( { ...value, [ key ]: ! value[ key ] } );

	const rows = [
		{
			key: 'admin_notify',
			title: __( 'Notify me when someone books', 'nemtly-booking' ),
			desc: __(
				'Email goes to the site admin address.',
				'nemtly-booking'
			),
		},
		{
			key: 'customer_confirm',
			title: __(
				'Send confirmation email to customer',
				'nemtly-booking'
			),
			desc: __(
				'Triggered immediately after a successful booking.',
				'nemtly-booking'
			),
		},
		{
			key: 'reminder',
			title: __( 'Send reminder email before event', 'nemtly-booking' ),
			desc: __(
				'Sent 24 hours before the event. Adjust later in Settings.',
				'nemtly-booking'
			),
		},
	];

	// Environment metadata sent when usage data sharing is on. Keep this list in
	// sync with Telemetry_Manager::custom_telemetry_notice() in PHP.
	const collected = [
		__( 'WordPress version', 'nemtly-booking' ),
		__( 'PHP version', 'nemtly-booking' ),
		__( 'MySQL version', 'nemtly-booking' ),
		__( 'Plugin version', 'nemtly-booking' ),
		__( 'Active plugins list', 'nemtly-booking' ),
		__( 'Inactive plugins list', 'nemtly-booking' ),
		__( 'Active theme name', 'nemtly-booking' ),
		__( 'Themes list', 'nemtly-booking' ),
		__( 'Site language', 'nemtly-booking' ),
		__( 'Site URL', 'nemtly-booking' ),
		__( 'Anonymous Nemtly usages data', 'nemtly-booking' ),
	];

	const renderSwitch = ( key ) => (
		<span
			className={ `nemtly-wizard-switch ${
				value[ key ] ? 'is-on' : 'is-off'
			}` }
			onClick={ () => toggle( key ) }
			role="switch"
			aria-checked={ value[ key ] }
			tabIndex={ 0 }
			onKeyDown={ ( e ) => {
				if ( e.key === ' ' || e.key === 'Enter' ) {
					e.preventDefault();
					toggle( key );
				}
			} }
		>
			<span className="nemtly-wizard-switch__thumb" />
		</span>
	);

	return (
		<WizardShell
			stepKey="notify"
			progress={ progress }
			onBack={ onBack }
			onSkip={ onSkip }
			onSkipSetup={ onSkipSetup }
			onContinue={ onContinue }
			isLoading={ isLoading }
		>
			<div className="nemtly-wizard-step">
				<h2 className="nemtly-wizard-headline">
					{ __( 'Notifications', 'nemtly-booking' ) }
				</h2>
				<p className="nemtly-wizard-lede">
					{ __(
						'Pick which emails Nemtly should send. You can customize the wording later.',
						'nemtly-booking'
					) }
				</p>

				<div className="nemtly-wizard-toggles">
					{ rows.map( ( row ) => (
						<label key={ row.key } className="nemtly-wizard-toggle">
							<div className="nemtly-wizard-toggle__text">
								<span className="nemtly-wizard-toggle__title">
									{ row.title }
								</span>
								<span className="nemtly-wizard-toggle__desc">
									{ row.desc }
								</span>
							</div>
							{ renderSwitch( row.key ) }
						</label>
					) ) }

					<div className="nemtly-wizard-toggle nemtly-wizard-toggle--stack">
						<div className="nemtly-wizard-toggle__row">
							<div className="nemtly-wizard-toggle__text">
								<span className="nemtly-wizard-toggle__title">
									{ __(
										'Help make Nemtly Booking even more awesome',
										'nemtly-booking'
									) }
								</span>
								<span className="nemtly-wizard-toggle__desc">
									{ __(
										'Allow Nemtly to collect diagnostic data and usage information. No personal or booking data is ever collected.',
										'nemtly-booking'
									) }{ ' ' }
									<button
										type="button"
										className="nemtly-wizard-link"
										onClick={ () =>
											setShowCollected(
												( shown ) => ! shown
											)
										}
										aria-expanded={ showCollected }
									>
										{ __(
											'What we collect',
											'nemtly-booking'
										) }
									</button>
								</span>
								{ showCollected && (
									<ul className="nemtly-wizard-collected">
										{ collected.map( ( item ) => (
											<li key={ item }>{ item }</li>
										) ) }
									</ul>
								) }
							</div>
							{ renderSwitch( 'telemetry' ) }
						</div>
					</div>
				</div>
			</div>
		</WizardShell>
	);
}
